import Geographic from '@/lib/geographic'
import polyline from '@mapbox/polyline'
import { httpPostJson } from '@/lib/net'
import { getOfflineMapService } from '@/services/offline-map-service/offline-map-service-instance'
import { offlineServiceState } from '@/services/offline-map-service'
import { captureException } from '@sentry/vue'
import type {
  CheckTilesetsForUpdatesPayload,
  CheckTilesetsForUpdatesResponse,
  DraftShape,
  GetZoomLevelsForEachTilesetByMapDetailLevelResponse,
} from '@/services/offline-map-service/types'

const checkTilesetsForUpdates = async (
  payload: CheckTilesetsForUpdatesPayload
): Promise<CheckTilesetsForUpdatesResponse | undefined> => {
  const url = `${window.environment.tilestoreUrl}/sync/check_tilesets_for_updates`
  try {
    return (await httpPostJson(url, payload)) as CheckTilesetsForUpdatesResponse
  } catch (e) {
    captureException(e as Error, {
      tags: {
        feature: 'offline-map-service',
        action: 'check_tilesets_for_updates',
      },
    })
  }
  return undefined
}

/**
 * Sets `offlineServiceState.draftMap.estimatedSize` to an estimate in bytes for the drafted offline map
 * used in **offline-create-card-form**, triggered when the `draftMap.shape` property is adjusted.
 *
 * Size in bytes is used to visually set the estimated download size that is displayed to the user while in the process of creating a new map.
 *
 * Delegates to the x-offline-maps package. State is kept in sync via the bridge
 * in offline-map-service-instance.ts ('estimate-updated' event).
 */
export const getDownloadSizeEstimate = async (): Promise<void> => {
  const { shape, mapDetail, name, notes } = offlineServiceState.draftMap

  if (!shape || !mapDetail) {
    offlineServiceState.draftMap.estimatedSize = 0
    return
  }

  getOfflineMapService().updateDraft({
    mapDetail,
    name,
    notes,
    shape: shape as [number, number][],
  })

  const latLonCoords: [number, number][] = shape.map((coord) => {
    const geographic = Geographic.fromDegreesTuple(coord)
    return [geographic.latitude, geographic.longitude]
  })

  offlineServiceState.draftMap.estimatedSize = 0
  await getOfflineMapService().getDownloadSizeEstimate(
    polyline.encode(latLonCoords)
  )
}

/**
 * Get tileset_update_info for a given set of tilesets and coordinates.
 * Used by marketing events to compute the estimated size of a map being deleted.
 *
 * Returns an Array of `tileset_update_info` for each of the passed in tilesets.
 */
export const getUpdateInfoForTilesets = async (
  tilesets: GetZoomLevelsForEachTilesetByMapDetailLevelResponse['tilesets'],
  coordinates: DraftShape
): Promise<
  CheckTilesetsForUpdatesResponse['tileset_update_info'] | undefined
> => {
  if (!coordinates) {
    return undefined
  }

  const latLonCoords: [number, number][] = coordinates.map((coord) => {
    const geographic = Geographic.fromDegreesTuple(coord)
    return [geographic.latitude, geographic.longitude]
  })

  const draftMapPolyline = polyline.encode(latLonCoords)

  const payload: CheckTilesetsForUpdatesPayload = {
    tileset_info: tilesets.map((tile) => ({
      tileset: tile.id,
      from_version: 0,
      z_min: tile.z_min,
      z_max: tile.z_max,
    })),
    region: draftMapPolyline,
  }

  return (await checkTilesetsForUpdates(payload))?.tileset_update_info
}
